import React, { useCallback, useEffect, useState } from 'react';
import { Trans } from '@lingui/react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Typography from '@mui/material/Typography';
import { CircularProgress, Stack } from '@mui/material';
import csv from 'csvtojson';
import { enqueueSnackbar } from 'notistack';
import { i18n } from 'i18n';
import {
  ApiException,
  MineModelDto,
  ProjectDto,
  StopesMutation,
  UnitSystem,
  useBulkUpdateNodesCsvMutation,
  useExportReticulationDataDufMutation,
  useExportReticulationNodesCsvMutation,
  useGetPourPresetGroupsQuery,
  useProjectPoursQuery,
  useReticulationDataExport,
  useUpdateStopeMutation
} from 'providers/api';
import { getKnownApiErrorMessage } from 'providers/api/apiErrorMessages';
import { UseQueryResult } from 'react-query';
import { HOUR_IN_MILLISECONDS } from 'config';
import useScenario from 'modules/project/providers/scenario/use';
import useSaveReticulationData from 'modules/project-admin/ProjectDetailsPage/useSaveReticulationData';
import { isReticulationImportFile } from 'modules/project-admin/ProjectDetailsPage/reticulationImportBody';
import { notNilOrEmpty } from 'utils';
import { Model } from '../../../hooks/useModelState';
import { exportReticulationToExcel } from '../../../MineModel/Export/exportReticulationToExcel';
import exportReticulationToDxf from '../../../MineModel/Export/exportReticulationToDxf';
import useDufImport from '../../hooks/useDufImport';
import useDxfImport from '../../hooks/useDxfImport';
import useExportReticulationNodesCsv from '../../hooks/useExportReticulationNodesCsv';
import useTunnelProfileImport, { TUNNEL_PROFILE_FILE_ACCEPT } from '../../hooks/useTunnelProfileImport';
import DufPreviewDialog from '../DufPreviewDialog/DufPreviewDialog';
import DxfPreviewDialog from '../DxfPreviewDialog/DxfPreviewDialog';
import LayerSelectionTree from '../LayerSelectionTree/LayerSelectionTree';
import PropertyMappingDialog from '../PropertyMappingDialog/PropertyMappingDialog';
import {
  buildStopeCsvRows,
  buildStopeUpdateFormsFromCsv,
  downloadJsonData,
  escapeCsvValue
} from './helpers';

const RETICULATION_STRING_FILE_ACCEPT = '.dxf,.duf';

const getFileExtension = (fileName: string) => fileName.split('.').pop()?.toLowerCase() ?? '';

const parseJson = (text: string): unknown => {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
};

const openFilePicker = (accept: string, onFileSelected: (file: File) => void) => {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.onchange = (e) => {
    const file = (e.target as HTMLInputElement).files?.[0];
    if (file) {
      onFileSelected(file);
    }
  };
  input.click();
};

interface ImportExportDialogsProps {
  importOpen: boolean;
  exportOpen: boolean;
  onImportClose: () => void;
  onExportClose: () => void;
  /** Called when DXF or DUF preview opens — parent can cancel competing modes. */
  onPreviewOpen?: () => void;
  /** Shows "Import Stope Shapes" when provided — the parent owns navigation to the bulk solids import scene. */
  onImportStopeShapes?: () => void;
  model: Model;
  project: ProjectDto;
  mineModel: MineModelDto;
  mineModelQuery: UseQueryResult<MineModelDto | undefined, unknown>;
  isAdminOrPc: boolean;
}

const ImportExportDialogs = ({
  importOpen,
  exportOpen,
  onImportClose,
  onExportClose,
  onPreviewOpen,
  onImportStopeShapes,
  model,
  project,
  mineModel,
  mineModelQuery,
  isAdminOrPc,
}: ImportExportDialogsProps) => {
  const [unitDialogOpen, setUnitDialogOpen] = useState(false);
  const [pendingDxfFile, setPendingDxfFile] = useState<File | null>(null);
  const [selectedUnit, setSelectedUnit] = useState<UnitSystem>(UnitSystem.Metric);
  const [triggerReticulationDataExport, setTriggerReticulationDataExport] = useState(false);
  const [pendingBackfillProExport, setPendingBackfillProExport] = useState(false);
  const [isNodesCsvImporting, setIsNodesCsvImporting] = useState(false);
  const [isStopeCsvImporting, setIsStopeCsvImporting] = useState(false);
  const [nodesCsvError, setNodesCsvError] = useState<string | null>(null);
  const [stopeCsvError, setStopeCsvError] = useState<string | null>(null);
  const { activeScenarioId } = useScenario();

  const exportReticulationNodesCsvMutation = useExportReticulationNodesCsvMutation(project.entityId as string);
  const exportReticulationDataDufMutation = useExportReticulationDataDufMutation(project.entityId as string);
  const bulkUpdateNodesCsvMutation = useBulkUpdateNodesCsvMutation(project.entityId as string);
  const updateStopeMutation = useUpdateStopeMutation(project.entityId as string);
  const projectPoursQuery = useProjectPoursQuery(
    {
      projectId: project.entityId as string,
      page: 1,
      pageSize: 10000,
      scenarioId: activeScenarioId ?? undefined,
    },
    { enabled: false },
  );
  const pourPresetGroupsQuery = useGetPourPresetGroupsQuery({
    projectId: project.entityId as string,
    page: 1,
    pageSize: 1000,
  });
  const exportReticulationNodesCsv = useCallback(
    (projectId: string) => exportReticulationNodesCsvMutation.mutateAsync(projectId),
    [exportReticulationNodesCsvMutation],
  );
  const { exportNodesCsv } = useExportReticulationNodesCsv(exportReticulationNodesCsv);
  const reticulationDataExport = useReticulationDataExport(
    { mineModelId: project.entityId as string, scenarioId: activeScenarioId ?? undefined },
    {
      staleTime: HOUR_IN_MILLISECONDS,
      enabled: triggerReticulationDataExport,
      onSettled: () => setTriggerReticulationDataExport(false),
    },
  );

  const {
    isImporting,
    error,
    isPreviewOpen,
    previewNodes,
    handleImportDxf,
    handleSaveDxfNodes,
    handleRemovePreview,
  } = useDxfImport({
    model,
    project,
    mineModel,
    mineModelQuery,
    isAdminOrPc,
  });

  const {
    isDufImporting,
    dufError,
    isDufPreviewOpen,
    previewNodesList,
    selectedLayerGuids,
    layerDialogOpen,
    availableLayers,
    modelEntities,
    propertyMappingDialogOpen,
    availableProperties,
    availableVertexAttributes,
    uniquePropertyValues,
    setSelectedLayerGuids,
    handleImportDuf,
    handleSaveDufNodes,
    handleRemoveDufPreview,
    handleLayerDialogCancel,
    handleLayerDialogConfirm,
    handlePropertyMappingConfirm,
    handlePropertyMappingCancel,
  } = useDufImport({
    model,
    project,
    mineModel,
    isAdminOrPc,
  });

  const { isTunnelProfileImporting, importTunnelProfile } = useTunnelProfileImport({ model, project });

  const {
    save: saveReticulation,
    isSubmitting: isSubmittingReticulation,
  } = useSaveReticulationData(
    () => mineModelQuery.refetch(),
    project?.entityId,
    project?.name,
    notNilOrEmpty(mineModel?.reticulationData),
  );

  useEffect(() => {
    if (pendingBackfillProExport && reticulationDataExport.data) {
      downloadJsonData(reticulationDataExport.data, 'reticulation-data.json');
      setPendingBackfillProExport(false);
    }
  }, [pendingBackfillProExport, reticulationDataExport.data]);

  useEffect(() => {
    if (isPreviewOpen || isDufPreviewOpen) {
      onPreviewOpen?.();
    }
  }, [isPreviewOpen, isDufPreviewOpen, onPreviewOpen]);

  const handleCloseUnitDialog = () => {
    setUnitDialogOpen(false);
    setPendingDxfFile(null);
  };

  const handleConfirmUnit = () => {
    if (pendingDxfFile) {
      handleImportDxf(pendingDxfFile, selectedUnit);
    }
    handleCloseUnitDialog();
  };

  const reticulationStringImporters: Record<string, (file: File) => void> = {
    dxf: (file) => {
      setPendingDxfFile(file);
      setUnitDialogOpen(true);
    },
    duf: handleImportDuf,
  };

  const handleImportReticulationStringClick = () => {
    onImportClose();
    openFilePicker(RETICULATION_STRING_FILE_ACCEPT, (file) => {
      const importer = reticulationStringImporters[getFileExtension(file.name)];
      if (importer) {
        importer(file);
      } else {
        enqueueSnackbar(i18n._('Unsupported file type. Select a DXF or DUF file.'), { variant: 'error' });
      }
    });
  };

  const handleImportStopeShapesClick = () => {
    onImportClose();
    onImportStopeShapes?.();
  };

  const handleImportTunnelProfileClick = () => {
    onImportClose();
    openFilePicker(TUNNEL_PROFILE_FILE_ACCEPT, importTunnelProfile);
  };

  const handleImportReticulationClick = () => {
    onImportClose();
    openFilePicker('.json', async (file) => {
      const data = parseJson(await file.text());
      if (!isReticulationImportFile(data)) {
        enqueueSnackbar(i18n._('The selected file is not a valid project file.'), { variant: 'error', autoHideDuration: 5000 });
        return;
      }
      saveReticulation(data);
    });
  };

  const handleImportNodesCsv = () => {
    if (!project.entityId) return;

    onImportClose();
    setNodesCsvError(null);

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        setIsNodesCsvImporting(true);
        await bulkUpdateNodesCsvMutation.mutateAsync({
          fileParam: {
            data: file,
            fileName: file.name,
          },
        });
      } catch {
        setNodesCsvError(i18n._('Failed to import nodes CSV. Please check the file format.'));
      } finally {
        setIsNodesCsvImporting(false);
      }
    };
    input.click();
  };

  const showStopeCsvError = (message: string) => {
    setStopeCsvError(message);
    enqueueSnackbar(message, { variant: 'error', autoHideDuration: 5000 });
  };

  const handleImportStopeCsv = () => {
    if (!project.entityId) return;

    onImportClose();
    setStopeCsvError(null);

    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.csv';
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      try {
        setIsStopeCsvImporting(true);
        const text = await file.text();
        const rows = await csv({ colParser: { volume: 'number' } }).fromString(text) as Record<string, unknown>[];

        const forms = buildStopeUpdateFormsFromCsv(
          rows,
          mineModel?.reticulationData?.stopeLocations ?? [],
          mineModel?.reticulationData?.stopes ?? [],
          mineModel?.reticulationData?.pipeData ?? [],
          (pourPresetGroupsQuery.data?.items ?? []).map((group) => ({
            entityId: group.entityId,
            name: group.name,
          })),
        );

        if (forms.length === 0) {
          showStopeCsvError(i18n._('No stope rows found to upload.'));
          return;
        }

        const mutation: StopesMutation = {
          projectName: project.name,
          data: forms,
        };

        await updateStopeMutation.mutateAsync(mutation);
        await mineModelQuery.refetch();
      } catch (importError) {
        const knownError = getKnownApiErrorMessage(importError);
        let errorMessage = i18n._('Failed to import stope CSV. Please check the file format.');
        if (knownError) {
          errorMessage = [knownError.title, ...(knownError.details ?? [])].join(' ');
        } else if (importError instanceof Error) {
          errorMessage = importError.message;
        }

        // A failed upload is already raised as a snackbar by the global mutation error handler.
        if (ApiException.isApiException(importError)) {
          setStopeCsvError(errorMessage);
        } else {
          showStopeCsvError(errorMessage);
        }
      } finally {
        setIsStopeCsvImporting(false);
      }
    };
    input.click();
  };

  const handleExportBackfillPro = () => {
    onExportClose();
    if (reticulationDataExport.data) {
      downloadJsonData(reticulationDataExport.data, 'reticulation-data.json');
    } else {
      setPendingBackfillProExport(true);
      setTriggerReticulationDataExport(true);
    }
  };

  const handleExportExcel = () => {
    onExportClose();
    if (!model?.nodes || !model?.stopes || !model?.spools || !model?.pipes || !model?.activeBackfillPlant) return;
    const data = exportReticulationToExcel(
      model.nodes,
      model.stopes,
      model.spools,
      model.pipes,
      model.activeBackfillPlant,
      !!model.project?.biDirectionalBoreholes,
    );
    downloadJsonData(data, 'reticulation-excel-export.json');
  };

  const handleExportDxf = () => {
    onExportClose();
    if (!model?.nodes || !model?.spools || !model?.pipes) return;
    const dxfContent = exportReticulationToDxf(model.nodes, model.spools, model.pipes);
    const blob = new Blob([dxfContent], { type: 'application/dxf' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'reticulation-export.dxf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportDuf = async () => {
    onExportClose();
    if (!project.entityId) return;

    const file = await exportReticulationDataDufMutation.mutateAsync(project.entityId);
    const url = window.URL.createObjectURL(file.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.fileName ?? `reticulation-export-${project.entityId}.duf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleExportNodesCsv = async () => {
    if (!project.entityId) return;
    onExportClose();
    setNodesCsvError(null);
    await exportNodesCsv(project.entityId);
  };

  const handleExportStopeCsv = async () => {
    onExportClose();

    const stopeLocations = mineModel?.reticulationData?.stopeLocations ?? [];
    const stopes = mineModel?.reticulationData?.stopes ?? [];
    const pipeData = mineModel?.reticulationData?.pipeData ?? [];

    if (stopeLocations.length === 0) {
      return;
    }

    const presetGroupNameById = new Map(
      (pourPresetGroupsQuery.data?.items ?? [])
        .map((group) => [group.entityId, group.name ?? group.entityId]),
    );

    const pourReports = projectPoursQuery.data?.items ?? (await projectPoursQuery.refetch()).data?.items ?? [];
    const rows = buildStopeCsvRows(stopeLocations, stopes, pipeData, presetGroupNameById, pourReports);
    if (rows.length === 0) {
      return;
    }

    const maxPipeCount = Math.max(1, ...stopes.map((stope) => stope.pipeArrays?.length ?? 0));
    const headers: string[] = [
      'stope_id',
      'stope_name',
      'node_id',
      'volume',
      'binder_percentage',
      'mass_concentration',
      'binder_tonnes',
      'level',
      'area',
      'stopeGeometryFile',
      'stopeGeometryFile.path',
      'stopeGeometryFile.display',
      'pour_preset_group_name',
    ];

    Array.from({ length: maxPipeCount }, (_, i) => i + 1).forEach((i) => {
      headers.push(`pipe_${i}_name`, `pipe_${i}_vertical_length`, `pipe_${i}_horizontal_length`);
    });

    const csvBody = rows
      .map((row) => headers.map((header) => escapeCsvValue(row[header])).join(','))
      .join('\r\n');

    const csvText = `${headers.join(',')}\r\n${csvBody}`;
    const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${project.name}-stope-data.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  return (
    <>
      <Dialog open={importOpen} onClose={onImportClose} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n._('Import from External')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleImportReticulationStringClick}
              disabled={isImporting || isDufImporting}
            >
              {isImporting || isDufImporting
                ? <Trans id="Importing">Importing...</Trans>
                : <Trans id="Import Reticulation String">Import Reticulation String</Trans>}
            </Button>
            {onImportStopeShapes && (
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={handleImportStopeShapesClick}
              >
                <Trans id="Import Stope Shapes">Import Stope Shapes</Trans>
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleImportTunnelProfileClick}
              disabled={isTunnelProfileImporting}
            >
              {isTunnelProfileImporting
                ? <Trans id="Importing">Importing...</Trans>
                : <Trans id="Import Tunnel Profile">Import Tunnel Profile</Trans>}
            </Button>
            {isAdminOrPc && (
              <Button
                variant="outlined"
                color="secondary"
                fullWidth
                onClick={handleImportReticulationClick}
                disabled={isSubmittingReticulation}
              >
                <Trans id="Import Project">Import Project</Trans>
              </Button>
            )}
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleImportNodesCsv}
              disabled={isNodesCsvImporting || isStopeCsvImporting || isImporting || isDufImporting}
            >
              {isNodesCsvImporting ? <Trans id="Importing">Importing...</Trans> : <Trans id="Import Nodes CSV">Import Nodes CSV</Trans>}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleImportStopeCsv}
              disabled={isStopeCsvImporting || isNodesCsvImporting || isImporting || isDufImporting}
            >
              {isStopeCsvImporting ? <Trans id="Importing">Importing...</Trans> : <Trans id="Import Stope CSV">Import Stope CSV</Trans>}
            </Button>
            {error && (
              <Typography color="error" variant="body2">{error}</Typography>
            )}
            {dufError && (
              <Typography color="error" variant="body2">{dufError}</Typography>
            )}
            {nodesCsvError && (
              <Typography color="error" variant="body2">{nodesCsvError}</Typography>
            )}
            {stopeCsvError && (
              <Typography color="error" variant="body2">{stopeCsvError}</Typography>
            )}
            <Typography variant="caption" color="warning">
              <Trans id="Use exported nodes CSV and edit properties only. Do not change node_id values.">
                Use exported nodes CSV and edit properties only. Do not change node_id values.
              </Trans>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onImportClose}>{i18n._('Cancel')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={exportOpen} onClose={onExportClose} maxWidth="xs" fullWidth>
        <DialogTitle>{i18n._('Export')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            {isAdminOrPc && (
              <>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={handleExportBackfillPro}
                >
                  <Trans id="Export to Backfill Pro">Export to Backfill Pro</Trans>
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  fullWidth
                  onClick={handleExportExcel}
                >
                  <Trans id="Export to Excel">Export to Excel</Trans>
                </Button>
              </>
            )}
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleExportDxf}
            >
              <Trans id="Export to DXF">Export to DXF</Trans>
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleExportDuf}
            >
              <Trans id="Export to DUF">Export to DUF</Trans>
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleExportNodesCsv}
              disabled={isNodesCsvImporting || isImporting || isDufImporting}
            >
              <Trans id="Export Nodes CSV">Export Nodes CSV</Trans>
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              fullWidth
              onClick={handleExportStopeCsv}
              disabled={projectPoursQuery.isFetching}
            >
              <Trans id="Export Stope CSV">Export Stope CSV</Trans>
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onExportClose}>{i18n._('Cancel')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={unitDialogOpen}
        onClose={handleCloseUnitDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{i18n._('Please Select Unit of DXF File')}</DialogTitle>
        <DialogContent dividers>
          <FormControl fullWidth>
            <InputLabel><Trans id="Unit">Unit</Trans></InputLabel>
            <Select
              value={selectedUnit ?? ''}
              onChange={(e) => setSelectedUnit(e.target.value as UnitSystem)}
            >
              <MenuItem value={UnitSystem.Metric}>{i18n._('Metric')}</MenuItem>
              <MenuItem value={UnitSystem.Imperial}>{i18n._('Imperial')}</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUnitDialog}>{i18n._('Cancel')}</Button>
          <Button
            onClick={handleConfirmUnit}
            variant="contained"
            disabled={selectedUnit === null || selectedUnit === undefined}
          >
            {i18n._('OK')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={layerDialogOpen} onClose={handleLayerDialogCancel} maxWidth="md" fullWidth>
        <DialogTitle>{i18n._('Select Layers')}</DialogTitle>
        <DialogContent dividers>
          <LayerSelectionTree
            availableLayers={availableLayers.map((l) => ({
              ...l,
              name: l.name ?? undefined,
            }))}
            modelEntities={modelEntities}
            selectedLayerGuids={selectedLayerGuids}
            setSelectedLayerGuids={setSelectedLayerGuids}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLayerDialogCancel}>{i18n._('Cancel')}</Button>
          <Button
            onClick={handleLayerDialogConfirm}
            variant="contained"
            disabled={selectedLayerGuids.length === 0}
          >
            {i18n._('OK')}
          </Button>
        </DialogActions>
      </Dialog>

      <PropertyMappingDialog
        open={propertyMappingDialogOpen}
        onClose={handlePropertyMappingCancel}
        onConfirm={handlePropertyMappingConfirm}
        availableProperties={availableProperties}
        availableVertexAttributes={availableVertexAttributes}
        availablePipeTypes={mineModel.reticulationData?.pipeData ?? []}
        uniquePropertyValues={uniquePropertyValues}
      />

      <Dialog
        open={isImporting || isDufImporting || isNodesCsvImporting || isStopeCsvImporting || isTunnelProfileImporting}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, textAlign: 'center', p: 3 },
        }}
      >
        <DialogContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <CircularProgress />
          <Typography variant="body1" sx={{ mt: 2 }}>
            {i18n._('Processing file, please wait...')}
          </Typography>
        </DialogContent>
      </Dialog>

      <DxfPreviewDialog
        isOpen={isPreviewOpen}
        onClose={handleRemovePreview}
        onSave={handleSaveDxfNodes}
        onRemove={handleRemovePreview}
        isSaving={isImporting}
        error={error}
        nodeCount={previewNodes.length}
      />

      <DufPreviewDialog
        isOpen={isDufPreviewOpen}
        onClose={handleRemoveDufPreview}
        onSave={() => handleSaveDufNodes()}
        onRemove={handleRemoveDufPreview}
        isSaving={isDufImporting}
        error={dufError}
        nodeCount={previewNodesList.length}
      />
    </>
  );
};

export default ImportExportDialogs;
